import React, { useState } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import type { ModalComponentProps } from '../../../components/modals/constants';
import { useSubmitJotCoachFeedbackMutation } from '../store/api';

const JotCoachFeedbackModal: React.FC<ModalComponentProps> = ({ modalProps, closeModal }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { itemType, itemId, initialUseful = null, itemContent, sessionId, noteId, sectionId } = modalProps as any;

  const [isUseful, setIsUseful] = useState<boolean | null>(initialUseful);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitFeedback, { isLoading }] = useSubmitJotCoachFeedbackMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async () => {
    // Validate that we have a useful rating
    if (isUseful === null) {
      enqueueSnackbar('Please select whether this was useful', { variant: 'warning' });
      return;
    }

    try {
      await submitFeedback({
        sessionId,
        submitFeedbackRequest: {
          feedback_items: [
            {
              target_type: itemType,
              target_id: itemId,
              is_useful: isUseful,
              comment: feedbackText || undefined,
              note_id: noteId || null,
              section_id: sectionId || null,
            },
          ],
        },
      }).unwrap();

      enqueueSnackbar('Thank you for your feedback!', { variant: 'success' });
      closeModal();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      enqueueSnackbar('Failed to submit feedback. Please try again.', { variant: 'error' });
    }
  };

  return (
    <>
      <DialogTitle>JotCoach Feedback</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Display the item content being reviewed */}
          {itemContent && (
            <Box
              sx={{
                bgcolor: '#e3f2fd',
                p: 2,
                borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                {itemContent.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {itemContent.description}
              </Typography>
            </Box>
          )}

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              Was this useful?
            </FormLabel>
            <RadioGroup
              value={isUseful === null ? '' : isUseful ? 'yes' : 'no'}
              onChange={(e) => {
                if (e.target.value === 'yes') setIsUseful(true);
                else if (e.target.value === 'no') setIsUseful(false);
              }}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Additional Feedback (optional)"
            placeholder="Tell us more about your experience..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          {isLoading ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </>
  );
};

export default JotCoachFeedbackModal;
